import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCircleInfo } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import ChoiceGroup from "../../crmpostventa-shared/components/ChoiceGroup";
import { EMPTY } from "../../crmpostventa-shared/crmFormat";
import { formatCampoValor } from "../fichaFormat";
import {
  AUTO_TAG,
  CANAL_GESTION_OPTIONS,
  CONFLICTO_TITULO,
  ERROR_GUARDAR_TITULO,
  MOTIVO_GESTION_OPTIONS,
  MOTIVO_MAX,
  OBSERVACION_MAX,
  TIPO_GESTION_LABEL,
  motivoGestionAyuda,
  plural,
} from "./fichaEditorModel";
import { controlId } from "./EditorStep";

const shown = (campo, value) => {
  const text = formatCampoValor({ ...campo, valor: value });
  return text === EMPTY ? <span className="crm-rollo__muted">{EMPTY}</span> : text;
};

function ResumenTabla({ secciones, cambios }) {
  return (
    <div className="crm-rollo__table-wrap">
      <table className="crm-rollo__table crm-ficha__fe-review">
        <thead>
          <tr>
            <th scope="col">Campo</th>
            <th scope="col">Antes</th>
            <th scope="col">Ahora</th>
          </tr>
        </thead>
        <tbody>
          {secciones.map((seccion) => {
            const filas = cambios.filter((cambio) => cambio.campo.seccion === seccion.clave);
            if (!filas.length) return null;
            return (
              <React.Fragment key={seccion.clave}>
                <tr className="crm-ficha__fe-review-sec">
                  <th colSpan={3} scope="colgroup">
                    {seccion.titulo}
                  </th>
                </tr>
                {filas.map(({ campo, antes, ahora, automatico }) => (
                  <tr key={campo.clave}>
                    <th scope="row">
                      {campo.etiqueta}
                      {automatico ? <span className="crm-ficha__auto">{AUTO_TAG}</span> : null}
                    </th>
                    <td className="crm-ficha__old" data-l="Antes">
                      {shown(campo, antes)}
                    </td>
                    <td className="crm-ficha__new" data-l="Ahora">
                      {shown(campo, ahora)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// "Registrar en Gestiones IPC como gestión finalizada" (informal save; FD3–FD6, FD8; Prompt 3 point 3, Prompt 6 point
// 6): Tipo in the dotted box of what the system fills, the motive (auto from the hand-edited sections, editable) and the
// canal (Otro by default).
function RegistroGestion({ cambios, registro }) {
  const checkId = controlId("registrar");
  const motivoId = controlId("gestion-motivo");
  const canalLabelId = controlId("canal-label");
  return (
    <>
      <label className="crm-ficha__fr-check">
        <input type="checkbox" id={checkId} checked={registro.marcado} onChange={(event) => registro.onMarcar(event.target.checked)} />
        <span>Registrar en Gestiones IPC como gestión finalizada</span>
      </label>
      {registro.marcado ? (
        <div className="crm-ficha__fr-reg">
          <div className="crm-ficha__fr-grid">
            <section className="crm-rollo__auto crm-ficha__fr-auto" aria-label="Datos automáticos">
              <div className="crm-rollo__auto-title">Se completa automáticamente</div>
              <dl>
                <div>
                  <dt>Tipo</dt>
                  <dd>{TIPO_GESTION_LABEL}</dd>
                </div>
              </dl>
            </section>
            <div className="crm-rollo__fld">
              <label className="crm-rollo__fld-label" htmlFor={motivoId}>
                Motivo de la gestión
              </label>
              <span className="crm-rollo__field crm-rollo__field--select">
                <select
                  className="crm-rollo__select"
                  id={motivoId}
                  value={registro.motivo}
                  aria-describedby={`${motivoId}-h`}
                  onChange={(event) => registro.onMotivo(event.target.value)}
                >
                  {MOTIVO_GESTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
              <span className="crm-rollo__help" id={`${motivoId}-h`}>
                {motivoGestionAyuda(cambios, registro.elegidoAMano)}
              </span>
            </div>
            <div className="crm-rollo__fld crm-ficha__fr-wide">
              <span className="crm-rollo__fld-label" id={canalLabelId}>
                Canal
              </span>
              <ChoiceGroup
                id={controlId("canal")}
                name={controlId("canal")}
                options={CANAL_GESTION_OPTIONS}
                value={registro.canal}
                labelledBy={canalLabelId}
                onChange={registro.onCanal}
              />
            </div>
          </div>
          <p className="crm-rollo__help crm-ficha__fr-help">
            <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" />
            La gestión se guarda como Resuelta en Finalizadas, con origen Ficha.
          </p>
        </div>
      ) : null}
    </>
  );
}

/**
 * Paso 2 de 2 of the Ficha editor (design Prompt 3): the changes by section, the motive (required) and an optional
 * observation, plus the save error and the conflict alerts. From a gestión (gestionText) it says the gestión is
 * finalized as Resuelta and a save error offers "Volver a la gestión" (gestionHref, through onLink); the informal save
 * shows the "Registrar en Gestiones IPC" checkbox (registro: { marcado, motivo, elegidoAMano, canal, onMarcar,
 * onMotivo, onCanal }).
 */
export default function RevisionStep({
  secciones,
  cambios,
  gestionText,
  registro,
  gestionHref,
  onLink,
  motivo,
  motivoError,
  observacion,
  observacionError,
  saving,
  saveError,
  conflicto,
  recargando,
  onMotivo,
  onObservacion,
  onReintentar,
  onVerCambiosNuevos,
  onCancelarConflicto,
}) {
  const motivoId = controlId("motivo");
  const observacionId = controlId("observacion");

  return (
    <>
      {saveError ? (
        <div className="crm-ficha__fe-alert">
          <Alert tone="error" role="alert" title={ERROR_GUARDAR_TITULO}>
            {saveError}
          </Alert>
          <div className="crm-ficha__fe-actions">
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={saving} onClick={onReintentar}>
              <FontAwesomeIcon icon={faArrowsRotate} aria-hidden="true" />
              Reintentar
            </button>
            {gestionHref ? (
              <a className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm" href={gestionHref} onClick={(event) => onLink?.(event, gestionHref)}>
                Volver a la gestión
              </a>
            ) : null}
          </div>
        </div>
      ) : null}
      {conflicto ? (
        <div className="crm-ficha__fe-alert">
          <Alert tone="error" role="alert" title={CONFLICTO_TITULO}>
            {conflicto}
          </Alert>
          <div className="crm-ficha__fe-actions">
            <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={recargando} onClick={onVerCambiosNuevos}>
              {recargando ? <span className="crm-rollo__spinner" aria-hidden="true" /> : null}
              Ver cambios nuevos
            </button>
            <button type="button" className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm" disabled={recargando} onClick={onCancelarConflicto}>
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      <fieldset className="crm-ficha__fe-fieldset" disabled={saving}>
        <section className="crm-ficha__fe-block" aria-labelledby="crm-ficha-fr-s">
          <div className="crm-ficha__fe-shead">
            <h3 id="crm-ficha-fr-s">Resumen de cambios</h3>
            <span className="crm-ficha__fe-count">{plural(cambios.length, "campo modificado", "campos modificados")}</span>
          </div>
          <ResumenTabla secciones={secciones} cambios={cambios} />
        </section>

        <section className="crm-ficha__fe-block" aria-labelledby="crm-ficha-fr-m">
          <h3 id="crm-ficha-fr-m">Motivo del cambio</h3>
          {gestionText ? (
            <p className="crm-ficha__fe-gtext">
              <FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" />
              <span>{gestionText}</span>
            </p>
          ) : null}
          <div className={`crm-rollo__fld${motivoError ? " has-error" : ""}`}>
            <label className="crm-rollo__fld-label" htmlFor={motivoId}>
              Motivo
              <span className="crm-rollo__req" aria-hidden="true">
                {" *"}
              </span>
            </label>
            <input
              className="crm-rollo__input"
              id={motivoId}
              value={motivo}
              maxLength={MOTIVO_MAX}
              aria-required="true"
              aria-invalid={motivoError ? true : undefined}
              aria-describedby={`${motivoId}-h`}
              placeholder="Ej.: el comercio cambió de banco y envió la constancia"
              onChange={(event) => onMotivo(event.target.value)}
            />
            {motivoError ? (
              <span className="crm-rollo__err" id={`${motivoId}-h`}>
                {motivoError}
              </span>
            ) : (
              <span className="crm-rollo__help" id={`${motivoId}-h`}>
                Contá por qué cambiás estos datos
              </span>
            )}
          </div>
          <div className={`crm-rollo__fld${observacionError ? " has-error" : ""}`}>
            <label className="crm-rollo__fld-label" htmlFor={observacionId}>
              Observación<span className="crm-rollo__opt"> (opcional)</span>
            </label>
            <textarea
              className="crm-rollo__input crm-rollo__textarea"
              id={observacionId}
              value={observacion}
              maxLength={OBSERVACION_MAX}
              placeholder="Algo que convenga dejar anotado"
              onChange={(event) => onObservacion(event.target.value)}
            />
            {observacionError ? <span className="crm-rollo__err">{observacionError}</span> : null}
          </div>
          {registro ? <RegistroGestion cambios={cambios} registro={registro} /> : null}
        </section>
      </fieldset>
    </>
  );
}
